import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import {
  getGuidanceService,
  createGuidancePaymentIntent,
  authorizeGuidanceBooking,
  confirmGuidanceBookingSelection,
} from '../services/guidanceBookingService';
import {
  TIMEZONE_OPTIONS,
  detectTimezone,
  computeThreeOptions,
  formatOptionLabel,
  blockStartToDisplayedTime,
  CALL_LEAD_MINUTES,
} from '../utils/guidanceScheduling';

const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

async function fetchStripePublishableKey() {
  try {
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const url = `${API_BASE_URL}/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key from integrations API, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

const pageStyle = {
  minHeight: '100vh',
  position: 'relative',
  background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
  color: 'white',
};

const containerStyle = {
  padding: '20px',
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '640px',
  margin: '0 auto',
};

const cardStyle = {
  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
  backdropFilter: 'blur(4px)',
  borderRadius: '12px',
  padding: '32px',
  border: '2px solid #10b981',
  marginBottom: '24px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: '2px solid rgba(16, 185, 129, 0.5)',
  background: 'rgba(0, 0, 0, 0.4)',
  color: 'white',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', marginBottom: '8px', color: '#4ade80', fontWeight: 600 };

const btnStyle = {
  background: 'linear-gradient(135deg, #10b981, #059669)',
  color: 'white',
  padding: '14px 28px',
  borderRadius: '8px',
  border: 'none',
  fontSize: '1.1rem',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
};

function PaymentStep({ service, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    let piId = null;
    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
        return;
      }
      if (!result.paymentIntent || result.paymentIntent.status !== 'requires_capture') {
        setError('Payment authorization was not successful. Please try again.');
        setProcessing(false);
        return;
      }
      piId = result.paymentIntent.id;
      await onPaid(piId);
      // Success: the parent switches to the time picker and this component unmounts.
    } catch (err) {
      setError(
        piId
          ? `Your card was authorized, but we couldn't record it — contact us with reference ${piId} if you don't hear from us. (${err.message || 'unknown error'})`
          : err.message || 'Payment failed'
      );
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div style={cardStyle}>
        <h3 style={{ color: '#10b981', marginBottom: '8px' }}>{service.name}</h3>
        <p style={{ color: '#d1d5db', marginBottom: '16px' }}>
          {service.duration} min • <span style={{ color: '#4ade80' }}>${Number(service.price).toFixed(2)}</span>
        </p>
        <PaymentElement />
        <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '12px', marginBottom: 0 }}>
          Your card will be authorized now but only charged once we confirm your time.
        </p>
        {error && <p style={{ color: '#fca5a5', marginTop: '12px' }}>{error}</p>}
      </div>
      <button type="submit" disabled={!stripe || processing} style={btnStyle}>
        {processing ? 'Processing…' : `Authorize $${Number(service.price).toFixed(2)}`}
      </button>
    </form>
  );
}

function PreferencesStep({ service, bookingId, stripePaymentIntentId, onDone }) {
  const { customer, isAuthenticated, getAuthHeaders } = useCustomerAuth();
  const [timezone, setTimezone] = useState(detectTimezone);
  const [windowStart, setWindowStart] = useState('09:00');
  const [windowEnd, setWindowEnd] = useState('17:00');
  const [revealed, setRevealed] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState({ customerName: '', customerEmail: '', customerPhone: '', notes: '' });
  const [whatsapp, setWhatsapp] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill from the logged-in account, if any — once, so it doesn't clobber their edits.
  useEffect(() => {
    if (isAuthenticated && customer) {
      const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
      setForm((f) => ({
        ...f,
        customerName: f.customerName || name || '',
        customerEmail: f.customerEmail || customer.email || '',
        customerPhone: f.customerPhone || customer.phone || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, customer]);

  const detectedTz = detectTimezone();
  const timezoneChoices = TIMEZONE_OPTIONS.some((t) => t.value === detectedTz)
    ? TIMEZONE_OPTIONS
    : [{ value: detectedTz, label: `Detected: ${detectedTz}` }, ...TIMEZONE_OPTIONS];

  const parseHour = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h + (m || 0) / 60;
  };

  const options = React.useMemo(() => {
    const startHour = parseHour(windowStart);
    const endHour = parseHour(windowEnd);
    const computed = computeThreeOptions({
      customerTz: timezone,
      startHour,
      endHour,
      durationMinutes: service.duration,
      availableDays: service.availableDays,
      availableHours: service.availableHours,
    });
    if (!computed) return null;
    return [
      { key: 'soonest', title: 'Soonest available', date: blockStartToDisplayedTime(computed.soonest) },
      { key: 'inWindow', title: 'Soonest in your preferred time', date: blockStartToDisplayedTime(computed.inWindow) },
      { key: 'laterInWindow', title: 'A bit further out, in your preferred time', date: blockStartToDisplayedTime(computed.laterInWindow) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, windowStart, windowEnd, service.duration, service.availableDays, service.availableHours]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!options) {
      setError('Could not compute available times. Please adjust your window and try again.');
      return;
    }
    if (!selectedKey) {
      setError('Please pick one of the 3 times.');
      return;
    }
    if (!form.customerName.trim() || !form.customerEmail.trim()) {
      setError('Please enter your name and email.');
      return;
    }
    if (!form.customerPhone.trim()) {
      setError('Phone number is required so we can call you.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const preferredTimeWindows = options.map((opt) => ({
        iso: opt.date.toISOString(),
        label: formatOptionLabel(opt.date, timezone),
        selected: opt.key === selectedKey,
      }));
      const selectedOption = options.find((opt) => opt.key === selectedKey);
      const data = await confirmGuidanceBookingSelection({
        bookingId,
        stripePaymentIntentId,
        selectedIso: selectedOption.date.toISOString(),
        preferredTimeWindows,
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        deliveryMethod: whatsapp ? 'whatsapp' : 'phone',
        notes: form.notes.trim() || undefined,
        _hp: honeypot,
        authHeaders: isAuthenticated ? getAuthHeaders() : {},
      });
      onDone(data);
    } catch (err) {
      setError(err.message || 'Booking failed');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
        <label htmlFor="guidance-hp">Leave blank</label>
        <input id="guidance-hp" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <div style={cardStyle}>
        <p style={{ color: '#86efac', marginBottom: '16px' }}>
          Card authorized — not charged yet. Tell us your timezone and when's usually good, then pick the time that works best. You'll be charged and booked in right away.
        </p>
        <label style={labelStyle} htmlFor="g-timezone">Your timezone</label>
        <select
          id="g-timezone"
          value={timezone}
          onChange={(e) => { setTimezone(e.target.value); setSelectedKey(null); }}
          style={{ ...inputStyle, marginBottom: '16px' }}
        >
          {timezoneChoices.map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>

        <label style={labelStyle}>What time of day usually works for you?</label>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <input
            type="time"
            value={windowStart}
            onChange={(e) => { setWindowStart(e.target.value); setSelectedKey(null); }}
            style={inputStyle}
            aria-label="Preferred window start"
          />
          <input
            type="time"
            value={windowEnd}
            onChange={(e) => { setWindowEnd(e.target.value); setSelectedKey(null); }}
            style={inputStyle}
            aria-label="Preferred window end"
          />
        </div>

        {!revealed && (
          <button type="button" onClick={() => setRevealed(true)} style={btnStyle}>
            Show available times
          </button>
        )}

        {revealed && (
          <>
            <label style={labelStyle}>Pick a time</label>
            <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '-4px', marginBottom: '12px' }}>
              Heads up: Chris may call up to {CALL_LEAD_MINUTES} minutes before your selected time, so please be ready a little early.
            </p>
            {!options && <p style={{ color: '#fca5a5' }}>Could not find available times — try adjusting your window.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {options && options.map((opt) => {
                const isSelected = selectedKey === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSelectedKey(opt.key)}
                    aria-pressed={isSelected}
                    style={{
                      textAlign: 'left',
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(16, 185, 129, 0.5)'}`,
                      background: isSelected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 0, 0, 0.4)',
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', color: '#22d3ee', marginBottom: '4px' }}>{opt.title}</div>
                    <div style={{ fontWeight: 600 }}>{formatOptionLabel(opt.date, timezone)}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={cardStyle}>
        {isAuthenticated && (
          <p style={{ color: '#86efac', fontSize: '0.85rem', marginTop: 0, marginBottom: '16px' }}>
            Signed in — this booking will show up under Bookings in your account, with a notification once it's confirmed.
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle} htmlFor="g-name">Name *</label>
            <input id="g-name" type="text" value={form.customerName} onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-email">Email *</label>
            <input id="g-email" type="email" value={form.customerEmail} onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-phone">Phone *</label>
            <input id="g-phone" type="tel" value={form.customerPhone} onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))} style={inputStyle} required />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', color: '#d1d5db', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />
              Call me on WhatsApp instead of a regular phone call
            </label>
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-notes">What would you like to talk about? (optional)</label>
            <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '-4px', marginBottom: '8px' }}>
              A light description helps Chris prepare, so you get the most out of your conversation.
            </p>
            <textarea id="g-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. I'd love guidance on..." style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} rows={3} />
          </div>
        </div>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.2)' }}>
          <p style={{ color: '#fca5a5', margin: 0 }}>{error}</p>
        </div>
      )}

      <button type="submit" disabled={submitting} style={btnStyle}>
        {submitting ? 'Booking…' : 'Confirm & book this time'}
      </button>
    </form>
  );
}

const GuidanceConversation = () => {
  const [service, setService] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paid, setPaid] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const svc = await getGuidanceService();
        if (cancelled) return;
        setService(svc);
        const [key, pi] = await Promise.all([fetchStripePublishableKey(), createGuidancePaymentIntent(svc.id)]);
        if (cancelled) return;
        if (!key) throw new Error('Payment is not configured right now. Please contact us directly.');
        setStripePromise(loadStripe(key));
        setClientSecret(pi.clientSecret);
        setStripePaymentIntentId(pi.paymentIntentId);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load booking page');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (success) {
    const confirmedTime = success.booking?.startTime
      ? new Date(success.booking.startTime).toLocaleString('en-AU', {
          timeZone: 'Australia/Brisbane',
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div style={{ ...cardStyle, textAlign: 'center', marginTop: '40px' }}>
            <h2 style={{ color: '#10b981', marginBottom: '16px' }}>You're booked in!</h2>
            <p style={{ color: '#d1d5db', lineHeight: 1.6, marginBottom: '16px' }}>
              {confirmedTime ? (
                <>We'll call you on <strong>{confirmedTime} AEST</strong>. A confirmation email with the calendar invite is on its way.</>
              ) : (
                'A confirmation email with the calendar invite is on its way.'
              )}
            </p>
            {success.bookingNumber && (
              <p style={{ color: '#4ade80', fontWeight: 600 }}>Reference: {success.bookingNumber}</p>
            )}
            <Link to="/" style={{ ...btnStyle, textDecoration: 'none', display: 'inline-block', width: 'auto', padding: '12px 24px', marginTop: '16px' }}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <header style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '20px' }}>
          <h1 style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', fontWeight: 'bold', marginBottom: '12px', color: '#10b981' }}>
            15-Minute Guidance Conversation
          </h1>
          <p style={{ color: '#22d3ee' }}>Tell us when's good, we'll call you — you're only charged once you pick a time</p>
        </header>

        {loadError && (
          <div style={{ ...cardStyle, borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.2)' }}>
            <p style={{ color: '#fca5a5', margin: 0 }}>{loadError}</p>
          </div>
        )}

        {!loadError && !service && <p style={{ color: '#22d3ee', textAlign: 'center' }}>Loading…</p>}

        {!loadError && service && !paid && clientSecret && stripePromise && (
          <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
            <PaymentStep
              service={service}
              onPaid={async (piId) => {
                setStripePaymentIntentId(piId);
                const auth = await authorizeGuidanceBooking({ serviceId: service.id, stripePaymentIntentId: piId });
                setBookingId(auth.bookingId);
                setPaid(true);
              }}
            />
          </Elements>
        )}

        {!loadError && service && paid && (
          <PreferencesStep service={service} bookingId={bookingId} stripePaymentIntentId={stripePaymentIntentId} onDone={setSuccess} />
        )}
      </div>
    </div>
  );
};

export default GuidanceConversation;
